import {
  motion,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
  useMotionValue,
} from 'framer-motion';
import { useRef, useEffect, useCallback } from 'react';
import { useScrollY } from '../../context/ScrollContext';
import './SectionCard.css';

const MOBILE_BREAKPOINT = 768;

// Per-level stacking values — index = how many cards have stacked on top of this one.
// Negative translateY shifts buried cards UP so their top edges peek above the active card.
// Relative peek per level: 6, 5, 4, 3, 3, 2 px — compresses as stack grows (realistic pile).
// Max 23px total peek fits within the 36px --header-card-gap zone.
const STACK_Y_PX      = [0, -4, -4, -4, -4, -4, -4];
const STACK_BRIGHTNESS = [1, 0.80, 0.63, 0.50, 0.40, 0.33, 0.28];
const STACK_LEVELS     = STACK_Y_PX.length - 1;

function lerp(a, b, t) { return a + (b - a) * t; }

function sampleTable(table, depth) {
  const lo = Math.min(Math.floor(depth), STACK_LEVELS - 1);
  return lerp(table[lo], table[lo + 1], depth - lo);
}

export default function SectionCard({
  children,
  index,
  isLast,
  isWide,
  scrollStart = 0,
  scrollStep = 900,
  onExcess,
}) {
  const scrollY = useScrollY();
  const shouldReduceMotion = useReducedMotion();
  const cardInnerRef = useRef(null);
  const cardBodyRef = useRef(null);
  const excessMV = useMotionValue(0);

  const measure = useCallback(() => {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    const inner = cardInnerRef.current;
    const body = cardBodyRef.current;
    if (!inner || !body) return;
    const excess = Math.max(0, body.scrollHeight - inner.clientHeight);
    excessMV.set(excess);
    onExcess?.(index, excess);
  }, [index, onExcess, excessMV]);

  useEffect(() => {
    const ro = new ResizeObserver(measure);
    if (cardBodyRef.current) ro.observe(cardBodyRef.current);
    measure();
    return () => ro.disconnect();
  }, [measure]);

  // Phase 1 (mobile only): translate card body up to reveal overflow content.
  // Uses [scrollY, excessMV] so it reacts when excess changes (e.g. language toggle).
  const contentY = useTransform([scrollY, excessMV], ([sv, ev]) => {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return 0;
    if (ev <= 0) return 0;
    const p = Math.max(0, Math.min(1, (sv - scrollStart) / ev));
    return -p * ev;
  });

  // Stack depth: 0 = active (nothing on top), 1 = one card stacked on top, etc.
  // Advances by 1 for each scrollStep of scroll past this card's phase start.
  const stackDepth = useTransform([scrollY, excessMV], ([sv, ev]) => {
    if (isLast || shouldReduceMotion) return 0;
    const excess = window.innerWidth < MOBILE_BREAKPOINT ? ev : 0;
    const phaseStart = scrollStart + excess;
    if (sv <= phaseStart || scrollStep <= 0) return 0;
    return Math.min((sv - phaseStart) / scrollStep, STACK_LEVELS);
  });

  // translateY: pushes buried cards down so their bottom peeks below the active card.
  const stackY = useTransform(stackDepth, (d) => sampleTable(STACK_Y_PX, d));

  // brightness: deeper cards appear darker, reinforcing visual depth.
  const brightness = useTransform(stackDepth, (d) => sampleTable(STACK_BRIGHTNESS, d));

  const filter = useMotionTemplate`brightness(${brightness})`;

  const cardClass = [
    'card-inner',
    index === 0 ? 'card-first' : '',
    isLast ? 'card-last' : '',
  ].filter(Boolean).join(' ');

  return (
    <motion.div
      ref={cardInnerRef}
      className={cardClass}
      data-scroll-start={scrollStart}
      style={{
        '--card-index': index,
        zIndex: 10 + index,
        y: stackY,
        filter,
      }}
    >
      <motion.div
        ref={cardBodyRef}
        className={`card-body${isWide ? ' card-body--wide' : ''}`}
        style={{ y: contentY }}
      >
        {isWide ? children : <div className="card-content-wrap">{children}</div>}
      </motion.div>
    </motion.div>
  );
}
